import { incrementingArray, shuffleArray } from './util'

const suits = ['hearts', 'diamonds', 'clubs', 'spades']
const [hearts, diamonds, clubs, spades] = suits

const redSuits = [hearts, diamonds]
const blackSuits = [clubs, spades]

const faceValues = [
  'ace',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
  '10',
  'jack',
  'queen',
  'king'
]

const cardBackImgSrc = '/assets/images/card-back.svg'

class CardMemoryGame {
  constructor ({ cardsPerFaceValue, totalCards, suits }) {
    this.cardsPerFaceValue = cardsPerFaceValue
    this.totalCards = totalCards
    this.suits = suits

    this.totalFaceValues = this.totalCards / this.cardsPerFaceValue
    this.currentTurn = []

    this.startButton = document.querySelector('button#js-start-exercise')
    this.cardsContainer = document.querySelector('div#js-12-cards-container')
    this.showExplainerButton = document.querySelector(
      'button#js-show-explainer'
    )

    this._revealOrHide = this._revealOrHide.bind(this)
    this.startExercise = this.startExercise.bind(this)
    this._showExplainer = this._showExplainer.bind(this)

    this.startButton.addEventListener('click', this.startExercise)
    this.showExplainerButton.addEventListener('click', this._showExplainer)
  }

  prepare () {
    this._shuffle()
    this._deal()
  }

  _shuffle () {
    const selectedValues = shuffleArray(faceValues).slice(0, this.totalFaceValues)
    this.cards = this.suits.flatMap((suit) =>
      selectedValues.map((value) => ({
        src: `/assets/images/${value}_of_${suit}.svg`,
        faceValue: value
      }))
    )
  }

  _deal () {
    this.cardsContainer.innerHTML = ''
    for (let i = 1; i <= this.totalCards; i++) {
      const image = document.createElement('img')
      image.src = cardBackImgSrc
      const card = document.createElement('div')
      card.id = `card-${i}`
      card.appendChild(image)
      this.cardsContainer.appendChild(card)
    }
    const cardSelectors = Array.from(incrementingArray(this.totalCards)).map(
      (i) => `card-${i}`
    )
    const shuffledCards = shuffleArray(this.cards)
    cardSelectors.forEach((selector, index) => {
      const card = document.querySelector(`div#${selector}`)
      card.dataset.src = shuffledCards[index].src
      card.dataset.faceValue = shuffledCards[index].faceValue
      card.dataset.tries = '' // to keep turning timestamps, to show a score at the end
      card.addEventListener('click', this._revealOrHide)
    })
  }

  startExercise (event) {
    event.target.classList.add('hidden')
    document.querySelector('div.explanation').classList.add('hidden')
    this.cardsContainer.classList.remove('hidden')
    this.cardsContainer.classList.add('grid')
    this.prepare()
  }

  _showExplainer (_event) {
    document.querySelector('div.explanation').classList.remove('hidden')
    this.showExplainerButton.classList.add('hidden')
  }

  endExercise () {
    const scoreElement = document.createElement('div')
    scoreElement.innerText = `Score: ${this._score()}%`
    this.cardsContainer.appendChild(scoreElement)

    this.startButton.classList.remove('hidden')
    this.showExplainerButton.classList.remove('hidden')
  }

  _score () {
    let totalTries = 0
    const cardsWithSingleTurn = {}
    const cards = [...this.cardsContainer.querySelectorAll('div')]
    cards.forEach((card) => {
      const turns = card.dataset.tries
        .split('|')
        .filter((entry) => entry.length > 0).length
      card.dataset.turns = turns
      totalTries += turns

      if (turns === 1) {
        cardsWithSingleTurn[card.dataset.faceValue] =
          (cardsWithSingleTurn[card.dataset.faceValue] ?? 0) + 1
      }
    })

    const totalTriesFactor = Math.max(1, totalTries / this.totalCards * 1.5)

    // lower means more luck
    // all matching face values correct at once? return 0 and score 0 points
    // no full sets correct at once? return 1 and do not impact points
    const luckPenalty =
      (Object.values(cardsWithSingleTurn).filter((val) => val === this.cardsPerFaceValue).length -
        this.totalFaceValues) *
      (-1 / this.totalFaceValues)

    const timeSpentPenalty = this._timeSpentPenalty()

    return (
      Math.round(
        10 * (((100 - timeSpentPenalty) * luckPenalty) / totalTriesFactor)
      ) / 10
    )
  }

  _areWeDoneYet () {
    return [...this.cardsContainer.querySelectorAll('div')].reduce(
      (accumulator, card) => accumulator && card.dataset.matched === 'true',
      true
    )
  }

  _revealOrHide (event) {
    const card = event.target.closest('div')
    const image = card.querySelector('img')
    // don't operate on cards that are already matched
    if (card.dataset.matched) {
      return
    }

    // display the face side if we haven't opened a pair
    if (this.currentTurn.length < this.cardsPerFaceValue) {
      if (!this.currentTurn.includes(card)) {
        image.style = this._humanizeLocation()
        image.src = card.dataset.src
        this.currentTurn.push(card)

        const tries = card.dataset.tries.split('|')
        tries.push(new Date().valueOf())
        card.dataset.tries = tries.join('|')
      }
      // we may now have a pair open
      if (this.currentTurn.length === this.cardsPerFaceValue) {
        const faceValues = this.currentTurn.map(
          (pairCard) => pairCard.dataset.faceValue
        )
        // check if we have a pair
        if (new Set(faceValues).size <= 1) {
          this.currentTurn.forEach((card) => (card.dataset.matched = true))
          this.currentTurn = []
          // keep them open, but remove them from 'currentPairs'
        }
        if (this._areWeDoneYet()) this.endExercise()
      }
    } else {
      if (this.currentTurn.includes(card)) {
        // show the back of both cards
        this.currentTurn.forEach((visibleCard) => {
          const image = visibleCard.querySelector('img')
          image.src = cardBackImgSrc
          image.style = this._humanizeLocation()
        })
        this.currentTurn = []
      }
    }
  }

  _humanizeLocation () {
    return `transform: rotate(${Math.random() * 10 - 5}deg); translate: ${Math.random() * 30 - 15}px ${Math.random() * 30 - 15}px;`
  }
}

export { suits, redSuits, blackSuits, faceValues, cardBackImgSrc, CardMemoryGame }
